import { API } from '@pibex/shared';
import type { Invitation, InvitationsResponse, MembersResponse, User } from '@pibex/shared';
import { useCallback, useEffect, useState } from 'react';
import { es } from '../../i18n/es.js';
import { api } from '../../lib/api.js';

export interface AdminData {
  members: User[];
  invitations: Invitation[];
}

export interface AdminDataState {
  data: AdminData | null;
  error: string | null;
  reload(): Promise<void>;
}

async function loadAdminData(): Promise<AdminData> {
  const [members, invitations] = await Promise.all([
    api.get<MembersResponse>(API.adminMembers),
    api.get<InvitationsResponse>(API.adminInvitations),
  ]);
  return { members: members.users, invitations: invitations.invitations };
}

/** Loads members + invitations together; `reload()` after every mutation. */
export function useAdminData(): AdminDataState {
  const [data, setData] = useState<AdminData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setData(await loadAdminData());
      setError(null);
    } catch {
      setError(es.adminPage.loadError);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadAdminData()
      .then((next) => {
        if (cancelled) return;
        setData(next);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError(es.adminPage.loadError);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, error, reload };
}
